import { zodResolver } from '@hookform/resolvers/zod';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { CodeSchema, FullPasswordSchema } from '../../../utils/schema';
import { CognitoErrorCodesEnum } from '../../../utils/types/eunms';
import { SimpleLayout } from '../../layouts/simple-layout';
import { PasswordInput } from '@components/molecules';
import { CodeInput } from '@components/molecules/inputs/prebuilts/code-input';
import type { ResponseResult } from '@utils';
import { WithControl } from '@utils';

export type PasswordResetFormValues = {
	code: string;
	password: string;
	repeatPassword: string;
};

export type ResetPasswordLabels = {
	title: string;
	resetMessage: (maskedEmail: string) => string;
	code: string;
	newPassword: string;
	repeatPassword: string;
	submit: string;
	sendNewCodeMessage: string;
	sendNewCodeButton: string;
	repeatPasswordErrorMessage: string;
	passwordErrors: {
		length: string;
		uppercase: string;
		lowercase: string;
		number: string;
	};
};

type Props = {
	email: string;
	labels: ResetPasswordLabels;
	onSubmit: (data: PasswordResetFormValues) => Promise<ResponseResult<any>>;
	resendCode: () => void;
};

export const ResetPasswordForm: React.FC<Props> = ({ labels, email, onSubmit, resendCode }) => {
	const maskedEmail: string = React.useMemo(() => {
		const [localPart, domainPart] = email.split('@');
		return `${localPart[0]}*****@${domainPart[0]}*****`;
	}, [email]);

	const schema = React.useMemo(() => {
		return z
			.object({
				password: z
					.string()
					.min(8, { message: labels.passwordErrors.length })
					.regex(/[A-Z]+/, { message: labels.passwordErrors.uppercase })
					.regex(/[a-z]+/, { message: labels.passwordErrors.lowercase })
					.regex(/[0-9]+/, { message: labels.passwordErrors.number }),
				repeatPassword: FullPasswordSchema,
				code: CodeSchema,
			})
			.superRefine((values, ctx) => {
				if (values.password !== values.repeatPassword) {
					return ctx.addIssue({
						code: z.ZodIssueCode.custom,
						message: labels.repeatPasswordErrorMessage,
						path: ['repeatPassword'],
					});
				}
			});
	}, []);

	const {
		handleSubmit,
		control,
		formState: { errors, dirtyFields, isSubmitting },
		setError,
	} = useForm<PasswordResetFormValues>({
		mode: 'onChange',
		defaultValues: {
			code: '',
			password: '',
			repeatPassword: '',
		},
		resolver: zodResolver(schema),
	});

	const onFormSubmit = async (data: any) => {
		const { error } = await onSubmit(data);

		if (error?.code === CognitoErrorCodesEnum.CODE_MISMATCH || error?.code === CognitoErrorCodesEnum.EXPIRED_CODE) {
			setError('code', { message: error.message });
		}
	};

	const repeatPasswordError =
		dirtyFields.repeatPassword && errors?.repeatPassword?.type === z.ZodIssueCode.custom
			? errors.repeatPassword?.message?.toString()
			: undefined;

	const ControlledPasswordInput = WithControl(PasswordInput, control);
	const ControlledCodeInput = WithControl(CodeInput, control);

	return (
		<SimpleLayout
			title={labels.title}
			subtitle={labels.resetMessage(maskedEmail)}
			submitButton={labels.submit}
			linkButton={labels.sendNewCodeButton}
			linkText={labels.sendNewCodeMessage}
			onLinkClick={resendCode}
			handleSubmit={handleSubmit(onFormSubmit)}
			isValid={dirtyFields.code && dirtyFields.password && dirtyFields.repeatPassword && !repeatPasswordError}
			isLoading={isSubmitting}
		>
			<div className="flex flex-col gap-14">
				<ControlledCodeInput label={labels.code} name="code" data-aid="code-input" error={errors?.code?.message} />
				<ControlledPasswordInput label={labels.newPassword} name="password" error={errors?.password?.message} />
				<ControlledPasswordInput label={labels.repeatPassword} name="repeatPassword" error={repeatPasswordError} />
			</div>
		</SimpleLayout>
	);
};
